import { useState, useEffect } from "react";

import "./sidebar.css";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { Box, Button } from "@mui/material";
import DifferenceIcon from "@mui/icons-material/Difference";
import axios from "axios";
import { useInfiniteQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import dayjs from "dayjs";

import Header from "../header/header";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

import MessageList from "./message-list";

import useCopilotStore from "@/store/copilotStore";
import useSidebarSettingsStore from "@/store/sidebarSettingsStore";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { updateConversation } from "@/api/copilot/copilot.api";
import useFileComparisonStore from "@/store/file-comparison-store";
import { generateUUID } from "@/utils/util";

import { DEFAULT_ADDITIONAL_PROMPT } from "@/constants/copilot";

const Sidebar = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [openRenameDialog, setOpenRenameDialog] = useState(false);

  const navigate = useNavigate();

  const { isCollapsed, toggleCollapse } = useSidebarSettingsStore();
  const {
    clearMessages,
    setCurrentConversationId,
    setChatLoading,
    setUuid,
    resetConversation,
  } = useCopilotStore();
  const fileComparisonStore = useFileComparisonStore();

  const startNewChat = () => {
    resetConversation();
    setChatLoading(false);
    navigate("/copilot");
  };

  const toggleSidebarCollapse = () => {
    toggleCollapse(!isCollapsed);
  };

  const renameConversation = async (conversationId: string, data: any) => {
    await updateConversation(conversationId, data);
    toast.success("Conversation has been renamed!", {
      position: "bottom-left",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
    });
    refetchChatHistory();
  };

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsSearchOpen((open) => !open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const fetchConversations = async ({ pageParam }: any) => {
    const accessToken = localStorage.getItem("accessToken");
    const userInfo = JSON.parse(localStorage.getItem("user_data") ?? "");
    const uuid = userInfo.id;
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}/conversations/history?user_id=${uuid}&cursor=${pageParam}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );
    return response?.data;
  };

  const {
    data: chatHistory,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchChatHistory,
  } = useInfiniteQuery({
    queryKey: ["chatHistory"],
    queryFn: fetchConversations,
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => lastPage.nextCursor,
  });

  const downloadConversation = async (conversation: any) => {
    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/conversation/download/${conversation?.id}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          responseType: "blob",
        }
      );

      if (response.status === 200) {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${conversation?.id}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success("Conversation has been downloaded!", {
          position: "bottom-left",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
      } else {
        console.error("Download failed:", response.status);
      }
    } catch (error) {
      console.error("Error downloading conversation:", error);
      toast.error("Failed to download conversation", {
        position: "bottom-left",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    }
  };

  const deleteConversation = async (conversation: any) => {
    try {
      const accessToken = localStorage.getItem("accessToken");
      const userInfo = JSON.parse(localStorage.getItem("user_data") ?? "");
      const uuid = userInfo.id;
      const data = {
        conversation_id: conversation?.id,
        is_cleared: true,
      };
      await axios.put(
        `${import.meta.env.VITE_API_URL}/conversation?user_id=${uuid}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      toast.success("Conversation has been deleted!", {
        position: "bottom-left",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      refetchChatHistory();
    } catch (error) {}
  };

  const handleSearchItemClick = (chat: any) => {
    clearMessages();
    resetConversation();
    navigate(`/copilot?conversationId=${chat?.id}`);
    setIsSearchOpen(false);
  };

  const navigateToChat = (chat: any) => {
    resetConversation();
    if (chat?.conversation_type === "compare") {
      navigate(`/copilot?conversationId=${chat?.id}&convType=compare`);
    } else {
      navigate(`/copilot?conversationId=${chat?.id}`);
    }
  };

  const refreshPage = () => {
    navigate(0);
  };

  return (
    <div className="app-container">
      <div className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
        <div className="sidebar-toggle">
          <Box className="menu-icon" onClick={toggleSidebarCollapse}>
            ☰
          </Box>
          {!isCollapsed && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Button
                    className="search-icon"
                    onClick={() => {
                      setIsSearchOpen(!isSearchOpen);
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="feather feather-search"
                    >
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="bg-black">
                  <p className="text-white flex">
                    Search
                    <div className="border rounded-sm px-1 mx-1">Ctrl + K</div>
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {!isCollapsed && (
          <Tabs defaultValue="chatHistory" className="ml-4">
            <TabsList className="mb-6">
              <TabsTrigger
                value="chatHistory"
                className="font-semibold min-w-20"
              >
                History
              </TabsTrigger>
              <TabsTrigger value="functions" className="font-semibold">
                Functions
              </TabsTrigger>
            </TabsList>
            <TabsContent value="chatHistory">
              <Button
                variant="contained"
                color="success"
                className="font-bold overflow-hidden whitespace-nowrap"
                onClick={startNewChat}
                sx={{
                  borderRadius: "2rem",
                  width: "12rem",
                  height: "3rem",
                  fontWeight: "600",
                  "&:hover": {
                    backgroundColor: "var(--colors-brand-tertiary)",
                    color: "white",
                  },
                }}
              >
                <AddIcon />
                <span>New Chat</span>
              </Button>

              <div className="recent-chats mt-8">
                <MessageList
                  messages={chatHistory?.pages.flatMap(
                    (page) =>
                      page.data.conversations?.map((c: any) => ({
                        ...c,
                        timestamp: c.last_modified_time,
                      })) || []
                  )}
                  navigateToChat={navigateToChat}
                  deleteConversation={deleteConversation}
                  openRenameDialog={openRenameDialog}
                  setOpenRenameDialog={setOpenRenameDialog}
                  renameConversation={renameConversation}
                  downloadConversation={downloadConversation}
                  hasNextPage={hasNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                  fetchNextPage={fetchNextPage}
                />
              </div>
            </TabsContent>
            <TabsContent value="functions">
              <Button
                variant="contained"
                color="success"
                className="font-bold overflow-hidden whitespace-nowrap"
                onClick={() => {
                  fileComparisonStore.setSessionId(generateUUID());
                  fileComparisonStore.setStatus("START");
                  navigate("/file-comparison");
                  refreshPage();
                }}
                sx={{
                  borderRadius: "2rem",
                  width: "12rem",
                  height: "3rem",
                  // fontSize: "1rem",
                  fontWeight: "600",
                  "&:hover": {
                    backgroundColor: "var(--colors-brand-tertiary)",
                    color: "white",
                  },
                }}
              >
                <DifferenceIcon />
                Compare Files
              </Button>
            </TabsContent>
          </Tabs>
        )}
      </div>
      <div className={`main-content ${isCollapsed ? "expanded" : ""}`}>
        <Header gcsPath="gs://dxcm_user_guide/Dexcom Phase 2 __ User Guide For Copilot.pdf" />
      </div>

      <CommandDialog open={isSearchOpen} onOpenChange={setIsSearchOpen}>
        <CommandInput placeholder="Search..." />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandGroup heading="Results">
            {chatHistory?.pages
              .flatMap(
                (page) =>
                  page.data.conversations?.map((c: any) => ({
                    ...c,
                    timestamp: c.last_modified_time,
                  })) || []
              )
              .map((chat: any) => (
                <CommandItem
                  key={chat?.id}
                  onSelect={() => handleSearchItemClick(chat)}
                >
                  <div className="flex justify-between w-full">
                    <div>{chat.title}</div>
                    <div>
                      {dayjs(chat.timestamp).format("MMM D, YYYY h:mm A")}
                    </div>
                  </div>
                </CommandItem>
              ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </div>
  );
};

export default Sidebar;
